/**
 * Numbers → French words.
 *
 * Sources:
 *
 * - Académie française, "Les rectifications de l'orthographe" (annex to the Dictionnaire):
 *   all numerals forming a complex number are joined by hyphens, "y compris ceux qui sont
 *   supérieurs à cent" (vingt-et-un, mille-six-cent-trente-cinq); "milliard, million et
 *   millier, étant des noms, ne sont pas concernés". The 1990 forms are admitted beside the
 *   traditional ones, not imposed.
 * - The agreement rule for vingt and cent (taken from the Académie, the Banque de
 *   dépannage linguistique and Larousse): they take an s only when they are multiplied and
 *   nothing follows them — quatre-vingts, deux cents, but quatre-vingt-un, deux cent un,
 *   quatre-vingt mille, deux cent mille; million and milliard are nouns, so deux cents
 *   millions. Mille is invariable.
 * - Regional forms: Belgium and Switzerland say septante and nonante; Vaud, Valais and
 *   Fribourg also say huitante, while Geneva, Neuchâtel and Jura keep quatre-vingts.
 *
 * The long scale is used: million 10^6, milliard 10^9, billion 10^12, billiard 10^15,
 * trillion 10^18, trilliard 10^21.
 */

import { isWholeDigits, splitGroups } from "./number-input";

export type FrenchRegion = "france" | "belgium" | "swissHuitante";
export type FrenchHyphenation = "traditional" | "reform";
export type FrenchGender = "masculine" | "feminine";

export interface FrenchOptions {
  region: FrenchRegion;
  hyphenation: FrenchHyphenation;
  /** The noun's gender decides un / une for a final 1. */
  gender: FrenchGender;
}

export type FrenchNote =
  | "et"
  | "quatreVingts"
  | "quatreVingtFollowed"
  | "cents"
  | "centFollowed"
  | "mille"
  | "longScale"
  | "de"
  | "regional";

export interface FrenchRegionalForm {
  region: FrenchRegion;
  words: string;
}

export interface FrenchCardinal {
  words: string;
  /** The same number in the other hyphenation, when it differs. */
  otherHyphenation: string | null;
  /** The number as the other regions say it, when it differs. */
  regionalForms: FrenchRegionalForm[];
  notes: FrenchNote[];
  needsDe: boolean;
}

const UNITS = [
  "",
  "un",
  "deux",
  "trois",
  "quatre",
  "cinq",
  "six",
  "sept",
  "huit",
  "neuf",
  "dix",
  "onze",
  "douze",
  "treize",
  "quatorze",
  "quinze",
  "seize",
] as const;

const TENS = [
  "",
  "",
  "vingt",
  "trente",
  "quarante",
  "cinquante",
  "soixante",
] as const;

/** Names of the 10^3, 10^6 … 10^21 groups: singular, plural. */
const SCALES: readonly (readonly [string, string])[] = [
  ["", ""],
  ["mille", "mille"],
  ["million", "millions"],
  ["milliard", "milliards"],
  ["billion", "billions"],
  ["billiard", "billiards"],
  ["trillion", "trillions"],
  ["trilliard", "trilliards"],
];

interface Context extends FrenchOptions {
  /** True when nothing numeric follows the group, so vingt/cent may take an s. */
  final: boolean;
}

function unWord(gender: FrenchGender, isUnit: boolean): string {
  return isUnit && gender === "feminine" ? "une" : "un";
}

/** Atoms for 1–99; the joiner decides which of them get hyphens. */
function underHundred(n: number, ctx: Context): string[] {
  const unitWord = (u: number) =>
    u === 1 ? unWord(ctx.gender, true) : UNITS[u];
  if (n <= 16) {
    return [n === 1 ? unWord(ctx.gender, true) : UNITS[n]];
  }
  if (n < 20) {
    return ["dix", UNITS[n - 10]];
  }
  const tens = Math.floor(n / 10);
  const unit = n % 10;
  if (tens <= 6) {
    if (unit === 0) {
      return [TENS[tens]];
    }
    return unit === 1
      ? [TENS[tens], "et", unWord(ctx.gender, true)]
      : [TENS[tens], unitWord(unit)];
  }
  return seventiesToNineties(tens, unit, ctx, unitWord);
}

function seventiesToNineties(
  tens: number,
  unit: number,
  ctx: Context,
  unitWord: (u: number) => string
): string[] {
  if (tens === 7) {
    return ctx.region === "france"
      ? soixanteDix(unit)
      : decadeWithUnit("septante", unit, ctx, unitWord);
  }
  if (tens === 8) {
    if (ctx.region === "swissHuitante") {
      return decadeWithUnit("huitante", unit, ctx, unitWord);
    }
    if (unit === 0) {
      return ctx.final ? ["quatre", "vingts"] : ["quatre", "vingt"];
    }
    return ["quatre", "vingt", unitWord(unit)];
  }
  return ctx.region === "france"
    ? ["quatre", "vingt", ...teen(unit)]
    : decadeWithUnit("nonante", unit, ctx, unitWord);
}

/** 70–79 in France: soixante + dix…dix-neuf, with "et" only for 71. */
function soixanteDix(unit: number): string[] {
  if (unit === 1) {
    return ["soixante", "et", "onze"];
  }
  return ["soixante", ...teen(unit)];
}

/** 10 + unit as atoms: dix, onze … seize, dix-sept … dix-neuf. */
function teen(unit: number): string[] {
  const n = 10 + unit;
  return n <= 16 ? [UNITS[n]] : ["dix", UNITS[unit]];
}

function decadeWithUnit(
  tens: string,
  unit: number,
  ctx: Context,
  unitWord: (u: number) => string
): string[] {
  if (unit === 0) {
    return [tens];
  }
  return unit === 1
    ? [tens, "et", unWord(ctx.gender, true)]
    : [tens, unitWord(unit)];
}

function joinAtoms(atoms: string[], style: FrenchHyphenation): string {
  let out = "";
  for (let i = 0; i < atoms.length; i++) {
    if (i > 0) {
      const spaced =
        style === "traditional" && (atoms[i] === "et" || atoms[i - 1] === "et");
      out += spaced ? " " : "-";
    }
    out += atoms[i];
  }
  return out;
}

/**
 * A group of 1–999 as one string. Hundreds and the rest are separated by a space in the
 * traditional spelling and by a hyphen in the reform one; hyphens inside the tens stay.
 */
function groupWords(v: number, ctx: Context): string {
  const h = Math.floor(v / 100);
  const rest = v % 100;
  const sep = ctx.hyphenation === "reform" ? "-" : " ";
  const parts: string[] = [];
  if (h > 0) {
    const cent = rest === 0 && h > 1 && ctx.final ? "cents" : "cent";
    parts.push(h > 1 ? `${UNITS[h]}${sep}${cent}` : cent);
  }
  if (rest > 0) {
    parts.push(joinAtoms(underHundred(rest, ctx), ctx.hyphenation));
  }
  return parts.join(sep);
}

interface GroupPlace {
  /** Index of the group: 0 units, 1 thousands, 2 millions … */
  index: number;
  value: number;
  /** The last spoken group, so in ordinal mode it carries the -ième ending. */
  isLast: boolean;
  /** The only group of the number (1 000 000 → "millionième"). */
  isOnly: boolean;
  ordinalMode: boolean;
}

/** A group from a million up: a count and the noun it counts. */
function scaledPhrase(place: GroupPlace, options: FrenchOptions): string {
  const { index, value, isLast, isOnly, ordinalMode } = place;
  const ending = ordinalMode && isLast;
  if (value === 1) {
    return ending && isOnly ? SCALES[index][0] : `un ${SCALES[index][0]}`;
  }
  const count = groupWords(value, {
    ...options,
    gender: "masculine",
    final: !ending,
  });
  return `${count} ${SCALES[index][ending ? 0 : 1]}`;
}

/** Words for one cardinal in a fixed set of options (no alternatives). */
function buildWords(
  digits: string,
  options: FrenchOptions,
  ordinalMode: boolean
): string {
  if (digits === "0") {
    return "zéro";
  }
  const groups = splitGroups(digits, 3);
  const joiner = options.hyphenation === "reform" ? "-" : " ";
  const lastIndex = groups.findIndex((v) => v > 0);
  // The part below a million is one complex number; each group from a million up is a noun phrase.
  const chunk: string[] = [];
  const phrases: string[] = [];
  for (let i = groups.length - 1; i >= 0; i--) {
    const value = groups[i];
    if (value === 0) {
      continue;
    }
    if (i >= 2) {
      const isOnly = i === groups.length - 1 && lastIndex === i;
      phrases.push(
        scaledPhrase(
          { index: i, value, isLast: i === lastIndex, isOnly, ordinalMode },
          options
        )
      );
    } else if (i === 1) {
      const thousands = groupWords(value, {
        ...options,
        gender: "masculine",
        final: false,
      });
      chunk.push(value === 1 ? "mille" : `${thousands}${joiner}mille`);
    } else {
      chunk.push(groupWords(value, { ...options, final: !ordinalMode }));
    }
  }
  return [...phrases, chunk.join(joiner)].filter((x) => x !== "").join(" ");
}

const REGIONS: FrenchRegion[] = ["france", "belgium", "swissHuitante"];

const RE_ET = /(?: |-)et(?: |-)/;
const RE_QUATRE_VINGTS = /quatre-vingts/;
const RE_QUATRE_VINGT_MORE = /quatre-vingt(?:-| mille)/;
const RE_CENTS = /\bcents\b|-cents\b/;
const RE_CENT_FOLLOWED = /\bcent(?:-| )\S/;
const RE_MILLE = /\bmille\b|-mille\b/;
const RE_ROUND_MILLIONS = /^[1-9]\d*000000$/;

const WORD_NOTES: readonly (readonly [FrenchNote, RegExp])[] = [
  ["et", RE_ET],
  ["quatreVingts", RE_QUATRE_VINGTS],
  ["quatreVingtFollowed", RE_QUATRE_VINGT_MORE],
  ["cents", RE_CENTS],
  ["centFollowed", RE_CENT_FOLLOWED],
  ["mille", RE_MILLE],
];

function collectNotes(
  digits: string,
  words: string,
  regionalCount: number
): FrenchNote[] {
  const notes: FrenchNote[] = WORD_NOTES.filter(([, pattern]) =>
    pattern.test(words)
  ).map(([note]) => note);
  if (digits.length >= 10) {
    notes.push("longScale");
  }
  if (RE_ROUND_MILLIONS.test(digits)) {
    notes.push("de");
  }
  if (regionalCount > 0) {
    notes.push("regional");
  }
  return notes;
}

/** The cardinal for a whole number from 0 to 10^24 − 1, given as decimal digits. */
export function frenchCardinal(
  digits: string,
  options: FrenchOptions
): FrenchCardinal {
  if (!isWholeDigits(digits)) {
    throw new RangeError("frenchCardinal: out of range");
  }
  const words = buildWords(digits, options, false);
  const otherStyle: FrenchHyphenation =
    options.hyphenation === "traditional" ? "reform" : "traditional";
  const other = buildWords(
    digits,
    { ...options, hyphenation: otherStyle },
    false
  );
  const regionalForms: FrenchRegionalForm[] = [];
  for (const region of REGIONS) {
    if (region === options.region) {
      continue;
    }
    const text = buildWords(digits, { ...options, region }, false);
    if (text !== words) {
      regionalForms.push({ region, words: text });
    }
  }
  return {
    words,
    otherHyphenation: other === words ? null : other,
    regionalForms,
    notes:
      digits === "0" ? [] : collectNotes(digits, words, regionalForms.length),
    needsDe:
      digits !== "0" && splitGroups(digits, 6)[0] === 0 && digits.length >= 7,
  };
}

const NON_ZERO_DIGIT = /[1-9]/;

/** Decimal digits read one by one: "trois virgule un quatre". */
export function frenchDecimalDigits(fraction: string): string {
  return [...fraction]
    .map((d) => (d === "0" ? "zéro" : UNITS[Number(d)]))
    .join(" ");
}

export interface FrenchNumber extends FrenchCardinal {
  decimal: boolean;
}

/** A signed, possibly decimal number: "moins trois virgule un quatre". */
export function frenchNumber(
  negative: boolean,
  integer: string,
  fraction: string,
  options: FrenchOptions
): FrenchNumber {
  const decimal = fraction !== "" && NON_ZERO_DIGIT.test(fraction);
  const whole = frenchCardinal(
    integer,
    decimal ? { ...options, gender: "masculine" } : options
  );
  const sign = negative ? "moins " : "";
  const withFraction = (text: string) =>
    decimal
      ? `${sign}${text} virgule ${frenchDecimalDigits(fraction)}`
      : `${sign}${text}`;
  return {
    ...whole,
    words: withFraction(whole.words),
    otherHyphenation:
      whole.otherHyphenation === null
        ? null
        : withFraction(whole.otherHyphenation),
    regionalForms: whole.regionalForms.map((form) => ({
      ...form,
      words: withFraction(form.words),
    })),
    notes: decimal ? [] : whole.notes,
    needsDe: !decimal && whole.needsDe,
    decimal,
  };
}

// ---------------------------------------------------------------------------
// Ordinals

export interface FrenchOrdinalOptions extends FrenchOptions {
  /** "second(e)" instead of "deuxième" for 2. */
  secondForTwo: boolean;
}

export interface FrenchOrdinal {
  words: string;
  abbreviation: string;
  otherHyphenation: string | null;
  /** "second" / "seconde" for 2, shown beside deuxième. */
  alternative: string | null;
}

const RE_LAST_WORD = /^(.*?)([^\s-]+)$/;
const SPECIAL_STEMS: Record<string, string> = {
  cinq: "cinquième",
  neuf: "neuvième",
};

/** Turn the last word of a cardinal into its ordinal form: quatre → quatrième. */
function ordinalWord(word: string): string {
  const special = SPECIAL_STEMS[word];
  if (special) {
    return special;
  }
  if (word.endsWith("e")) {
    return `${word.slice(0, -1)}ième`;
  }
  return `${word}ième`;
}

function ordinalWords(digits: string, options: FrenchOptions): string {
  const cardinal = buildWords(
    digits,
    { ...options, gender: "masculine" },
    true
  );
  const match = RE_LAST_WORD.exec(cardinal);
  if (match === null) {
    return cardinal;
  }
  return `${match[1]}${ordinalWord(match[2])}`;
}

function ordinalAbbreviation(digits: string, gender: FrenchGender): string {
  if (digits === "1") {
    return gender === "feminine" ? "1re" : "1er";
  }
  return `${digits}e`;
}

/** The ordinal for a whole number from 1 to 10^24 − 1, or null for 0. */
export function frenchOrdinal(
  digits: string,
  options: FrenchOrdinalOptions
): FrenchOrdinal | null {
  if (!isWholeDigits(digits) || digits === "0") {
    return null;
  }
  const feminine = options.gender === "feminine";
  let words: string;
  let alternative: string | null = null;
  if (digits === "1") {
    words = feminine ? "première" : "premier";
  } else if (digits === "2") {
    const second = feminine ? "seconde" : "second";
    words = options.secondForTwo ? second : "deuxième";
    alternative = options.secondForTwo ? "deuxième" : second;
  } else {
    words = ordinalWords(digits, options);
  }
  const otherStyle: FrenchHyphenation =
    options.hyphenation === "traditional" ? "reform" : "traditional";
  const other =
    digits.length === 1 && digits !== "0"
      ? words
      : ordinalWords(digits, { ...options, hyphenation: otherStyle });
  return {
    words,
    abbreviation: ordinalAbbreviation(digits, options.gender),
    otherHyphenation:
      other === words || digits === "1" || digits === "2" ? null : other,
    alternative,
  };
}
